import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import StatusPill from '../components/StatusPill';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';
import CalendarPage from './CalendarPage';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';
import { invoiceDisplayNumber } from '../utils/invoiceDoc';
import { printInvoice } from '../modals/InvoiceViewModal';

const GRID = '2fr 1fr 1.6fr 1.1fr .9fr .9fr .6fr';

const PAY_LABELS = {
  stripe: '💳 Card (Stripe)',
  viva:   '🔵 Viva Wallet',
  paypal: '🅿️ PayPal',
  cash:   '💵 Cash on pickup',
  bank:   '🏦 Bank Transfer',
  online: '🌐 Pre-paid online',
};

function InfoRow({ label, value, mono, style }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--bd)' }}>
      <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 8, fontSize: 10 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', fontFamily: mono ? "'DM Mono',monospace" : undefined, ...style }}>{value}</span>
    </div>
  );
}

function ColHead({ title }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6, marginTop: 2 }}>
      {title}
    </div>
  );
}

function resolveRate(v, groups, vehicleModels) {
  if (!v) return 0;
  // 1) Model price wins when assigned
  if (v.modelId) {
    const m = (vehicleModels || []).find(mm => mm.id === v.modelId);
    if (m && m.priceDay !== '' && m.priceDay != null) return parseFloat(m.priceDay) || 0;
  }
  // 2) Group price
  if (v.useGroupPricing && v.groupId) {
    const g = (groups || []).find(gg => gg.id === v.groupId);
    if (g) return g.priceDay || 0;
  }
  // 3) Individual price
  return v.priceDay || 0;
}

// ── date helpers ──────────────────────────────────────────────────────────────
// Local YYYY-MM-DD — NOT toISOString() (that converts to UTC and shifts the day
// for timezones ahead of UTC, e.g. Cyprus UTC+2/+3, breaking date navigation).
function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function isoToday()    { return ymd(new Date()); }
function isoTomorrow() { const d = new Date(); d.setDate(d.getDate() + 1); return ymd(d); }
function fmtDayLabel(iso) {
  const today = isoToday(), tom = isoTomorrow();
  if (iso === today) return 'Today';
  if (iso === tom)   return 'Tomorrow';
  return new Date(iso + 'T00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
// Returns true if rental r is active/booked on isoDate
function rentalCoversDate(r, iso) {
  if (!r.start || !r.end || r.status === 'complete') return false;
  const sel = new Date(iso + 'T00:00');
  const s   = new Date(r.start); s.setHours(0,0,0,0);
  const e   = new Date(r.end);   e.setHours(23,59,59,999);
  return sel >= s && sel <= e;
}

export default function RentalsPage({ defaultTab }) {
  const { rentals, setRentals, vehicles, groups, vehicleModels, setDetailPanel, openModal, setRentalDraft, maintHist, users, roles, getUserName, isOperatorRole, currentUserId, assignRental, unassignRental, canEdit, showToast, companySettings, invoices, generateLeaseInvoice, resolveAgreementTemplate } = useApp();

  // Resolve template + context for a rental, then print or open in a new tab.
  // Saved snapshot (agreementSaved) wins; otherwise live-resolves the current template.
  function openContractForRental(r, autoPrint) {
    const v = vehicles.find(vv => vv.id === r.vid);
    const tmpl = r.agreementSaved?.body
      ? { name: r.agreementSaved.templateName, body: r.agreementSaved.body, clauses: r.agreementSaved.clauses }
      : (resolveAgreementTemplate ? resolveAgreementTemplate(r.vid, r.type === 'lease' ? 'lease' : 'rental') : null);
    const ctx = r.agreementSaved?.context || buildContractContext({
      rental: r, vehicle: v, company: companySettings,
      signatureDataUrl: r.signData || r.presignData || r.signatureDataUrl,
      signedAt: r.signedAt || r.presignedAt,
    });
    printContract(tmpl, ctx, { title: `Contract ${r.id || ''}`, autoPrint });
  }
  const leasingEnabled = companySettings?.enableLeasing !== false;
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');
  // Bucket filter triggered by stat-card clicks: '' | 'booked' | 'active' | 'due_today' | 'overdue'
  // Lives alongside filterStatus so the dropdown and the cards don't fight each other.
  const [filterBucket, setFilterBucket] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedId, setExpandedId]     = useState(null);
  const [tab, setTab]                   = useState(defaultTab || 'list');
  const [selectedDate, setSelectedDate] = useState(isoToday);
  const [editingRental, setEditingRental] = useState(null); // rental id being edited
  const [editDates, setEditDates] = useState({ start: '', end: '', t1: '', t2: '' });

  const getVehicle = (vid) => vehicles.find(v => v.id === vid);

  function beginEditRental(r) {
    setEditDates({
      start: r.start ? r.start.slice(0, 10) : '',
      end: r.end ? r.end.slice(0, 10) : '',
      t1: r.t1 || '', t2: r.t2 || '',
    });
    setEditingRental(r.id);
  }

  function saveEditRental(rid) {
    const fmtLabel = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    setRentals(prev => prev.map(r => {
      if (r.id !== rid) return r;
      const newStart = editDates.start ? editDates.start + 'T' + (editDates.t1 || '09:00') : r.start;
      const newEnd   = editDates.end   ? editDates.end   + 'T' + (editDates.t2 || '09:00') : r.end;
      const days = Math.max(1, Math.round((new Date(newEnd) - new Date(newStart)) / 86400000));
      return { ...r, start: newStart, end: newEnd, t1: editDates.t1, t2: editDates.t2, startLabel: fmtLabel(newStart), endLabel: fmtLabel(newEnd), calSpan: days };
    }));
    setEditingRental(null);
    showToast('✅ Booking dates updated');
  }

  // Vehicles currently in maintenance (check maintHist directly — reliable source of truth)
  const maintVids = useMemo(() =>
    new Set(maintHist.filter(m => m.status === 'in_progress').map(m => m.vid)),
    [maintHist]
  );

  const now         = new Date();
  const todayISOStr = isoToday();

  // All stats scoped to TODAY
  const todayRentals = rentals.filter(r => rentalCoversDate(r, todayISOStr));
  // Booked today = pickup today or earlier, not yet checked in
  const statBooked   = todayRentals.filter(r => r.status === 'booked').length;
  // Active today = checked in, currently out on rent
  const statActive   = todayRentals.filter(r => r.status === 'active' && r.end && new Date(r.end) > now).length;
  // Due today = active and returning today
  const statDueToday = todayRentals.filter(r => r.status === 'active' && r.end && r.end.startsWith(todayISOStr)).length;
  // Overdue = active but end date already passed
  const statOverdue  = todayRentals.filter(r => r.status === 'active' && r.end && new Date(r.end) < now).length;
  // Available today = vehicles not occupied and not in maintenance
  const occupiedTodayVids = new Set(todayRentals.map(r => r.vid));
  const statAvail    = vehicles.filter(v => !occupiedTodayVids.has(v.id) && !maintVids.has(v.id)).length;

  // ── Leasing stats ─────────────────────────────────────────────────
  const activeLeases = rentals.filter(r => r.type === 'lease' && r.status === 'active');
  const statLeaseCount = activeLeases.length;
  const statLeaseMrr   = activeLeases.reduce((s, r) => s + (parseFloat(r.monthlyRate) || 0), 0);

  const q = search.toLowerCase();
  const shownVids = new Set();

  // Rentals that cover selectedDate
  const dayRentals = useMemo(() =>
    rentals.filter(r => rentalCoversDate(r, selectedDate)),
    [rentals, selectedDate]
  );

  // Vehicle IDs occupied on selectedDate
  const occupiedVids = useMemo(() =>
    new Set(dayRentals.map(r => r.vid)),
    [dayRentals]
  );

  const isOp = isOperatorRole();

  const filteredRentals = useMemo(() => dayRentals.filter(r => {
    // Operators only see rentals assigned to them
    if (isOp && r.assignedTo !== currentUserId) return false;
    const v = getVehicle(r.vid);
    const matchQ = !q || r.customer.toLowerCase().includes(q)
      || (v?.name || '').toLowerCase().includes(q)
      || (v?.plate || '').toLowerCase().includes(q)
      || r.id.toLowerCase().includes(q);
    const matchSt = !filterStatus || r.status === filterStatus;
    const matchTp = !filterType  || v?.type === filterType;
    // Bucket filter (from stat cards) — overrides nothing, just adds an extra AND condition
    let matchBucket = true;
    if (filterBucket === 'booked')    matchBucket = r.status === 'booked';
    else if (filterBucket === 'active')    matchBucket = r.status === 'active';
    else if (filterBucket === 'due_today') matchBucket = r.status === 'active' && r.end && r.end.startsWith(todayISOStr);
    else if (filterBucket === 'overdue')   matchBucket = r.status === 'active' && r.end && new Date(r.end) < now;
    return matchQ && matchSt && matchTp && matchBucket;
  }), [dayRentals, q, filterStatus, filterType, filterBucket, todayISOStr, now, isOp, currentUserId]);

  const availableVehicles = useMemo(() => {
    return vehicles.filter(v => {
      if (filterStatus && filterStatus !== 'available') return false;
      if (maintVids.has(v.id)) return false;  // exclude vehicles in maintenance
      if (occupiedVids.has(v.id)) return false;   // occupied on selected date → not free
      if (filterType && v.type !== filterType) return false;
      if (q && !v.name.toLowerCase().includes(q) && !v.plate.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [vehicles, occupiedVids, filterStatus, filterType, q, maintVids]);

  function toggle(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  // Check if a string looks like an image (base64 or URL)
  function isImage(s) {
    return typeof s === 'string' && (s.startsWith('data:image') || s.startsWith('http') || s.startsWith('/') || s.startsWith('blob:'));
  }

  return (
    <div>
      <PageHeader
        title={tab === 'calendar' ? 'Calendar' : 'Rentals'}
        icon={tab === 'calendar' ? '📅' : '📋'}
        subtitle={tab === 'calendar'
          ? 'Visual timeline — drag to reschedule, click to view details.'
          : 'Active rentals, bookings, and returns. Click any row for full details.'}
        actions={canEdit && canEdit('rentals') && (
          <>
            {leasingEnabled && (
              <button className="btn btn-ghost" onClick={() => openModal('add-lease')}>📅 + Add Lease</button>
            )}
            <button className="btn btn-primary" onClick={() => openModal('add-rental')}>+ Add Rental</button>
          </>
        )}
      />
      {/* Stats — clickable as filters (toggle on/off) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className={leasingEnabled ? 'sg sg-5' : 'sg sg-4'} style={{ flex: 1, marginBottom: 0 }}>
          <StatCard
            label="Booked" value={statBooked}
            desc={filterBucket === 'booked' ? 'Filtering — click to clear' : 'Need check-in today'}
            active={filterBucket === 'booked'}
            onClick={() => setFilterBucket(filterBucket === 'booked' ? '' : 'booked')}
          />
          <StatCard
            label="Active" value={statActive}
            desc={filterBucket === 'active' ? 'Filtering — click to clear' : 'Currently out on rent'}
            active={filterBucket === 'active'}
            onClick={() => setFilterBucket(filterBucket === 'active' ? '' : 'active')}
          />
          <StatCard
            label="Due Today" value={statDueToday}
            desc={filterBucket === 'due_today' ? 'Filtering — click to clear' : 'Expected return today'}
            active={filterBucket === 'due_today'}
            onClick={() => setFilterBucket(filterBucket === 'due_today' ? '' : 'due_today')}
          />
          <StatCard
            label="Overdue" value={statOverdue} variant="am"
            desc={filterBucket === 'overdue' ? 'Filtering — click to clear' : 'Past return date'}
            active={filterBucket === 'overdue'}
            onClick={() => setFilterBucket(filterBucket === 'overdue' ? '' : 'overdue')}
          />
          {leasingEnabled && (
            <StatCard
              label="Active Leases"
              value={statLeaseCount}
              desc={statLeaseMrr ? `€${statLeaseMrr.toLocaleString()} MRR` : 'No active leases'}
              variant="bl"
            />
          )}
        </div>
        {/* Available badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'center',
          background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 20,
          padding: '6px 14px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          boxShadow: 'var(--sh)',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--gr)', display: 'inline-block' }} />
          {statAvail} available
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['list', '📋  List'], ['calendar', '📅  Calendar']].map(([id, lbl]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '6px 20px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: '1px solid var(--bd)', cursor: 'pointer',
              background: tab === id ? 'var(--ac)' : 'var(--sur)',
              color: tab === id ? '#fff' : 'var(--tx2)',
              transition: 'all .15s',
            }}
          >{lbl}</button>
        ))}
      </div>

      {/* ── CALENDAR TAB ── */}
      {tab === 'calendar' && <CalendarPage />}

      {/* ── LIST TAB ── */}
      {tab === 'list' && <>

      {/* ── Daily date picker ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => {
          const d = new Date(selectedDate + 'T00:00'); d.setDate(d.getDate()-1);
          setSelectedDate(ymd(d));
        }}>‹</button>
        <button
          className="btn btn-sm"
          onClick={() => setSelectedDate(isoToday())}
          style={{ background: selectedDate === isoToday() ? 'var(--ac)' : 'var(--sur)', color: selectedDate === isoToday() ? '#fff' : 'var(--tx2)', border: '1px solid var(--bd)' }}
        >Today</button>
        <button
          className="btn btn-sm"
          onClick={() => setSelectedDate(isoTomorrow())}
          style={{ background: selectedDate === isoTomorrow() ? 'var(--ac)' : 'var(--sur)', color: selectedDate === isoTomorrow() ? '#fff' : 'var(--tx2)', border: '1px solid var(--bd)' }}
        >Tomorrow</button>
        <input
          type="date"
          className="fsel"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ fontSize: 12 }}
        />
        <button className="btn btn-ghost btn-sm" onClick={() => {
          const d = new Date(selectedDate + 'T00:00'); d.setDate(d.getDate()+1);
          setSelectedDate(ymd(d));
        }}>›</button>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', marginLeft: 4 }}>
          📅 {fmtDayLabel(selectedDate)}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, fontSize: 11, color: 'var(--tx3)' }}>
          <span style={{ color: 'var(--ac)', fontWeight: 600 }}>{filteredRentals.length} occupied</span>
          <span>·</span>
          <span style={{ color: 'var(--gr)', fontWeight: 600 }}>{availableVehicles.length} free</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="tb2">
        <div className="srch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Search asset, plate, customer..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="fsel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="overdue">Overdue</option>
          <option value="available">Available</option>
          <option value="booked">Booked</option>
        </select>
        <select className="fsel" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          <option value="Car">Cars</option>
          <option value="Van">Vans</option>
          <option value="Machine">Machines</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 11, color: 'var(--tx3)' }}>Completed:</span>
          <div
            onClick={() => setShowCompleted(s => !s)}
            style={{
              padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, cursor: 'pointer',
              background: showCompleted ? 'var(--gr)' : 'var(--bd)',
              color: showCompleted ? '#fff' : 'var(--tx2)',
            }}
          >
            {showCompleted ? 'ON' : 'OFF'}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: GRID }}>
          <div className="th">Asset</div>
          <div className="th">Status</div>
          <div className="th">Customer</div>
          <div className="th">Pickup → Return</div>
          <div className="th">Price</div>
          <div className="th">Contract</div>
          <div className="th"></div>
        </div>

        {filteredRentals.length === 0 && availableVehicles.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>
            No vehicles match your filter
          </div>
        )}

        {filteredRentals.length > 0 && (
          <div style={{ padding: '8px 14px 4px', background: 'var(--sur2)', borderBottom: '1px solid var(--bd)', fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            🔴 Occupied — {fmtDayLabel(selectedDate)} ({filteredRentals.length})
          </div>
        )}

        {filteredRentals.map(r => {
          const v = getVehicle(r.vid);
          shownVids.add(r.vid);
          const isActive  = r.status === 'active' || r.status === 'overdue';
          const isOpen    = expandedId === r.id;

          // Photos: only show strings that look like actual images
          const photosPre = (r.photosPre || []).filter(isImage);
          const photosRet = (r.photosRet || []).filter(isImage);
          const hasPhotos = photosPre.length > 0 || photosRet.length > 0;

          const prepaid     = r.depositPaid || r.deposit || 0;
          const outstanding = Math.max(0, r.agreedPrice - prepaid);

          // Real payments breakdown from linked pro formas (online + desk split)
          const linkedInv  = invoices.filter(i => i.contract === r.id || i.contractId === r.id);
          const paidInv    = linkedInv.filter(i => i.status === 'paid');
          const paidTotal  = paidInv.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
          const dueAtDeskNow = Math.max(0, (parseFloat(r.agreedPrice) || 0) - paidTotal);
          // Cash actually collected = sum of PAID cash pro formas (self-corrects stale r.cashAmount)
          const cashPaid   = paidInv.filter(i => i.method === 'cash').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

          return (
            <div key={r.id} style={{ borderBottom: '1px solid var(--bd)' }}>
              {/* Main row */}
              <div
                style={{
                  display: 'grid', gridTemplateColumns: GRID,
                  padding: '10px 14px', alignItems: 'center', gap: 8,
                  cursor: 'pointer', background: isOpen ? 'var(--sur2)' : 'transparent',
                  transition: 'background .15s',
                }}
                onClick={() => toggle(r.id)}
              >
                {/* Asset */}
                <div>
                  <div className="an" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{v?.icon} {v?.name || r.vid}</span>
                    {r.type === 'lease' && (
                      <span style={{
                        fontSize: 8, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        background: 'var(--bl-l)', color: 'var(--bl)', border: '1px solid var(--bl-b)',
                        letterSpacing: '0.05em', whiteSpace: 'nowrap',
                      }}>
                        📅 LEASE
                      </span>
                    )}
                  </div>
                  <div className="ap">
                    {v?.type?.toUpperCase()} · {v?.plate || r.vid}
                    {r.type === 'lease' && r.monthlyRate ? ` · €${r.monthlyRate}/month` : ''}
                  </div>
                </div>

                {/* Status */}
                <div><StatusPill status={r.status} /></div>

                {/* Customer — name + phone + assignment */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{r.customer}</div>
                  {r.phone && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>{r.phone}</div>}
                  {r.assignedTo ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: users.find(u => u.id === r.assignedTo)?.color || 'var(--tx3)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 700 }}>
                        {users.find(u => u.id === r.assignedTo)?.initials || '?'}
                      </span>
                      <span style={{ fontSize: 9, color: 'var(--bl)', fontWeight: 600 }}>{getUserName(r.assignedTo)}</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 9, color: 'var(--am)', fontWeight: 600, marginTop: 2 }}>⚠️ Unassigned</div>
                  )}
                </div>

                {/* Pickup → Return */}
                <div>
                  {/* Pickup row */}
                  <div style={{ fontSize: 11, color: 'var(--tx2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 9, color: 'var(--gr)', fontWeight: 700 }}>↑</span>
                    <span>{r.startLabel || '—'}{r.t1 ? ` · ${r.t1}` : ''}</span>
                  </div>
                  {/* Return row */}
                  <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, ...(r.status === 'overdue' ? { color: 'var(--ac)', fontWeight: 600 } : { color: 'var(--tx2)' }) }}>
                    <span style={{ fontSize: 9, color: r.status === 'overdue' ? 'var(--ac)' : 'var(--tx3)', fontWeight: 700 }}>↓</span>
                    <span>{r.endLabel || '—'}{r.t2 ? ` · ${r.t2}` : ''}</span>
                  </div>
                  {/* Pickup location */}
                  {r.pickupLocName && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                      📍 {r.pickupLocName}
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="pv">€{r.agreedPrice}</div>

                {/* Contract */}
                <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{r.id}</div>

                {/* Actions */}
                <div className="abtns" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {isActive
                    ? <button className="ab ab-end" onClick={() => openModal(r.type === 'lease' ? 'end-lease' : 'end-rental', r.vid)}>End</button>
                    : r.status === 'booked'
                      ? <button className="ab ab-st" onClick={() => { setRentalDraft(r); openModal('check-in'); }}>Start</button>
                      : <span style={{ fontSize: 10, color: 'var(--gr)' }}>✓</span>
                  }
                  <span style={{ fontSize: 11, color: 'var(--tx3)', marginLeft: 2, transition: 'transform .2s', display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                </div>
              </div>

              {/* Expanded panel */}
              {isOpen && (
                <div style={{
                  background: 'var(--sur2)',
                  borderTop: '1px solid var(--bd)',
                  padding: '14px 16px 16px',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>

                    {/* ── Col 1: Customer ── */}
                    <div>
                      <ColHead title="Customer" />
                      <InfoRow label="Name"        value={r.customer} style={{ fontWeight: 700 }} />
                      <InfoRow label="Phone"       value={r.phone} />
                      {r.email && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--bd)' }}>
                          <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 8, fontSize: 10 }}>Email</span>
                          <a href={`mailto:${r.email}`} style={{ fontWeight: 500, color: 'var(--bl)', textAlign: 'right' }}>{r.email}</a>
                        </div>
                      )}
                      <InfoRow label="Passport/ID" value={r.passport}    mono />
                      <InfoRow label="Licence"     value={r.licence}     mono />
                      <InfoRow label="Nationality" value={r.nationality} />

                      {/* Signature */}
                      <div style={{ marginTop: 10 }}>
                        <ColHead title="Agreement & Signature" />
                        {r.presigned && r.presignData ? (
                          <>
                            <div style={{ fontSize: 10, color: 'var(--gr)', fontWeight: 600, marginBottom: 4 }}>✅ Pre-signed online — {r.presignedAt}</div>
                            <img src={r.presignData} alt="Signature" style={{ width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 6, maxHeight: 55, objectFit: 'contain' }} />
                          </>
                        ) : r.signed ? (
                          <div style={{ fontSize: 11, color: 'var(--gr)' }}>✓ Signed at check-in</div>
                        ) : (
                          <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Not signed yet — signs at check-in</div>
                        )}
                        {/* View / Print contract — always available (even unsigned, so it can be printed blank for the customer to sign on paper) */}
                        <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 10, padding: '3px 8px' }}
                            onClick={e => { e.stopPropagation(); openContractForRental(r, false); }}
                          >
                            📋 View
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 10, padding: '3px 8px' }}
                            onClick={e => { e.stopPropagation(); openContractForRental(r, true); }}
                          >
                            🖨️ Print {!(r.signed || r.presigned) && <span style={{ color: 'var(--am)' }}>· unsigned</span>}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Col 2: Financial + Dates ── */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <ColHead title="Rental Dates" />
                        {editingRental !== r.id && canEdit('rentals') && r.status === 'booked' && (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 9, padding: '2px 8px' }} onClick={e => { e.stopPropagation(); beginEditRental(r); }}>✏️ Edit</button>
                        )}
                      </div>
                      {editingRental === r.id ? (
                        <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            <div>
                              <label style={{ fontSize: 9, color: 'var(--tx3)', fontWeight: 600 }}>Pickup Date</label>
                              <input type="date" className="fi" value={editDates.start} onChange={e => setEditDates(d => ({ ...d, start: e.target.value }))} style={{ fontSize: 10, padding: '3px 5px' }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 9, color: 'var(--tx3)', fontWeight: 600 }}>Pickup Time</label>
                              <input type="time" className="fi" value={editDates.t1} onChange={e => setEditDates(d => ({ ...d, t1: e.target.value }))} style={{ fontSize: 10, padding: '3px 5px' }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 9, color: 'var(--tx3)', fontWeight: 600 }}>Return Date</label>
                              <input type="date" className="fi" value={editDates.end} onChange={e => setEditDates(d => ({ ...d, end: e.target.value }))} style={{ fontSize: 10, padding: '3px 5px' }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 9, color: 'var(--tx3)', fontWeight: 600 }}>Return Time</label>
                              <input type="time" className="fi" value={editDates.t2} onChange={e => setEditDates(d => ({ ...d, t2: e.target.value }))} style={{ fontSize: 10, padding: '3px 5px' }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 9 }} onClick={() => setEditingRental(null)}>Cancel</button>
                            <button className="btn btn-primary btn-sm" style={{ fontSize: 9 }} onClick={() => saveEditRental(r.id)}>Save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <InfoRow label="Pickup"   value={r.startLabel} mono />
                          <InfoRow label="Return"   value={r.endLabel}   mono style={r.status === 'overdue' ? { color: 'var(--ac)', fontWeight: 600 } : {}} />
                          {r.t1 && <InfoRow label="Pickup time"  value={r.t1} />}
                          {r.t2 && <InfoRow label="Return time"  value={r.t2} />}
                        </>
                      )}
                      {r.pickupLocName && <InfoRow label="📍 Pickup"  value={r.pickupLocName} />}
                      {r.returnLocName && r.returnLocName !== r.pickupLocName && <InfoRow label="📍 Return" value={r.returnLocName} />}

                      <div style={{ marginTop: 10 }}>
                        <ColHead title="Financial" />
                        {r.type === 'lease' ? (() => {
                          // ── LEASE FINANCIAL VIEW ────────────────────────
                          const leaseInvoices = invoices.filter(i => i.contract === r.id && i.isLeaseInvoice);
                          const paidCount = leaseInvoices.filter(i => i.status === 'paid').length;
                          const issuedCount = leaseInvoices.filter(i => ['issued', 'pending', 'overdue'].includes(i.status)).length;
                          const draftCount = leaseInvoices.filter(i => i.status === 'draft').length;
                          const invoicedCount = (r.invoicedMonths || []).length;
                          const remainingMonths = Math.max(0, (r.termMonths || 0) - invoicedCount);
                          const paidAmount = leaseInvoices
                            .filter(i => i.status === 'paid')
                            .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                          const outstandingMonthly = leaseInvoices
                            .filter(i => i.status !== 'paid')
                            .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                          const progressPct = r.termMonths ? Math.round((invoicedCount / r.termMonths) * 100) : 0;
                          return (
                            <>
                              <InfoRow label="Monthly rate" value={`€${r.monthlyRate || 0}/month`}
                                style={{ color: 'var(--bl)', fontWeight: 600 }} />
                              <InfoRow label="Term" value={`${r.termMonths} months`} />
                              <InfoRow label="Total contract" value={`€${(r.agreedPrice || 0).toLocaleString()}`}
                                style={{ color: 'var(--gr)', fontSize: 13, fontWeight: 700 }} mono />
                              <InfoRow label="Payment" value={PAY_LABELS[r.payMethod] || r.payMethod} />
                              {r.deposit > 0 && (
                                <InfoRow label="Deposit" value={`€${r.deposit}${r.depositMethod ? ' · ' + r.depositMethod : ''}`} mono />
                              )}
                              {/* Progress bar */}
                              <div style={{ margin: '8px 0 4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 3 }}>
                                  <span style={{ color: 'var(--tx3)' }}>Billing progress</span>
                                  <span style={{ color: 'var(--tx2)', fontWeight: 600 }}>{invoicedCount} / {r.termMonths} months</span>
                                </div>
                                <div style={{ height: 5, background: 'var(--bd)', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ width: `${progressPct}%`, height: '100%', background: 'var(--bl)', borderRadius: 3, transition: 'width .3s' }} />
                                </div>
                              </div>
                              {/* Money summary */}
                              <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 5, padding: '7px 9px', margin: '6px 0', fontSize: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: 'var(--tx3)' }}>Paid so far</span>
                                  <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{paidAmount.toFixed(0)} ({paidCount} mo)</span>
                                </div>
                                {outstandingMonthly > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                                    <span style={{ color: 'var(--tx3)' }}>Awaiting payment</span>
                                    <span style={{ color: 'var(--am)', fontWeight: 600 }}>€{outstandingMonthly.toFixed(0)} ({issuedCount + draftCount} inv)</span>
                                  </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                                  <span style={{ color: 'var(--tx3)' }}>Remaining months</span>
                                  <span style={{ color: 'var(--bl)', fontWeight: 600 }}>{remainingMonths} mo · €{(remainingMonths * (r.monthlyRate || 0)).toFixed(0)}</span>
                                </div>
                              </div>
                              {/* Next invoice date + last invoice status */}
                              {(() => {
                                if (remainingMonths === 0) return (
                                  <div style={{ fontSize: 11, color: 'var(--gr)', marginTop: 4, padding: '8px 10px', background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 6 }}>
                                    ✅ Lease complete — all {r.termMonths} months invoiced.
                                  </div>
                                );
                                // Calculate the next billing date based on cadence + months already invoiced
                                const start = r.start ? new Date(r.start) : null;
                                if (!start) return null;
                                const monthsBilled = (r.invoicedMonths || []).length;
                                const nextBillingDate = new Date(start.getFullYear(), start.getMonth() + monthsBilled, start.getDate());
                                const cadenceLabel = {
                                  advance:      'in advance (before month starts)',
                                  end_of_month: 'at the end of each month',
                                  net_15:       'NET-15 (15 days to pay)',
                                  net_30:       'NET-30 (30 days to pay)',
                                }[r.billingCadence] || 'monthly';
                                // Last issued invoice for this lease
                                const lastInvoice = leaseInvoices
                                  .slice()
                                  .sort((a, b) => (b.issueDate || '').localeCompare(a.issueDate || ''))[0];
                                return (
                                  <div style={{ marginTop: 6, padding: '10px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, fontSize: 11, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                                      <div>
                                        <span style={{ color: 'var(--tx3)' }}>📅 Next invoice: </span>
                                        <strong style={{ color: 'var(--bl)' }}>{nextBillingDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                                        <span style={{ color: 'var(--tx3)', fontSize: 10, marginLeft: 6 }}>· {cadenceLabel}</span>
                                      </div>
                                    </div>
                                    {lastInvoice && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, paddingTop: 5, borderTop: '1px solid var(--bd)' }}>
                                        <div>
                                          <span style={{ color: 'var(--tx3)' }}>Last invoice: </span>
                                          <strong style={{ fontFamily: "'DM Mono',monospace" }}>{lastInvoice.num}</strong>
                                          <span style={{ color: 'var(--tx3)', fontSize: 10, marginLeft: 6 }}>· {lastInvoice.issued}</span>
                                        </div>
                                        <span style={{
                                          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, textTransform: 'uppercase',
                                          background: lastInvoice.status === 'paid' ? 'var(--gr-l)' : lastInvoice.status === 'overdue' ? 'var(--ac-l)' : 'var(--am-l)',
                                          color:      lastInvoice.status === 'paid' ? 'var(--gr)' : lastInvoice.status === 'overdue' ? 'var(--ac)' : 'var(--am)',
                                        }}>
                                          {lastInvoice.status}
                                        </span>
                                      </div>
                                    )}
                                    <div style={{ fontSize: 10, color: 'var(--tx3)', fontStyle: 'italic', marginTop: 2 }}>
                                      💡 Invoices are generated automatically by the system on the billing date.
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Renew lease and other action buttons */}
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                                {/* Renew Lease — show when lease is in last 2 months OR already complete */}
                                {canEdit && canEdit('rentals') && (() => {
                                  const monthsLeft = (r.termMonths || 0) - ((r.invoicedMonths || []).length);
                                  const endDate = r.end ? new Date(r.end) : null;
                                  const daysToEnd = endDate ? Math.ceil((endDate - new Date()) / 86400000) : 999;
                                  const canRenew = r.status === 'complete' || monthsLeft <= 2 || daysToEnd <= 60;
                                  if (!canRenew) return null;
                                  return (
                                    <button
                                      className="btn btn-blue btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Build preset from existing lease — new start date = old end + 1 day
                                        const oldEnd = r.end ? new Date(r.end) : new Date();
                                        const newStart = new Date(oldEnd.getFullYear(), oldEnd.getMonth(), oldEnd.getDate() + 1)
                                          .toISOString().slice(0, 10);
                                        openModal('add-lease', {
                                          isRenewal: true,
                                          previousLeaseId: r.id,
                                          customerType: r.customerCompany ? 'company' : 'individual',
                                          customerId: r.customerId,
                                          customerName: r.customer,
                                          customerCompany: r.customerCompany,
                                          vatNumber: r.vatNumber,
                                          phone: r.phone,
                                          email: r.email,
                                          vid: r.vid,
                                          start: newStart,
                                          termMonths: r.termMonths,
                                          monthlyRate: r.monthlyRate,
                                          deposit: r.deposit,
                                          annualMileageCap: r.annualMileageCap,
                                          includedServices: r.includedServices,
                                          notes: r.notes,
                                          payMethod: r.payMethod,
                                          depositMethod: r.depositMethod,
                                          billingCadence: r.billingCadence,
                                          vatMode: r.vatMode,
                                          vatRate: r.vatRate,
                                          pickupLocId: r.pickupLocId,
                                          agreementId: r.agreementId,
                                        });
                                      }}
                                    >
                                      🔄 Renew Lease
                                    </button>
                                  );
                                })()}
                              </div>
                              {r.includedServices && (
                                <div style={{ marginTop: 6, fontSize: 10, color: 'var(--tx3)', fontStyle: 'italic' }}>
                                  Included: {r.includedServices}
                                </div>
                              )}
                            </>
                          );
                        })() : (
                          // ── SHORT-TERM RENTAL FINANCIAL VIEW (unchanged) ────
                          <>
                            <InfoRow label="Rate"         value={`€${r.rate || 0}/day × ${r.calSpan || ''} days`} />
                            {/* Payments breakdown FIRST — one line per paid pro forma (e.g. online card + desk cash) */}
                            {paidInv.length > 0 && (
                              <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 5, padding: '6px 8px', margin: '5px 0', fontSize: 10 }}>
                                {paidInv.map(inv => (
                                  <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 5 }}>
                                    <span style={{ color: 'var(--tx3)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {inv.methodLabel || PAY_LABELS[inv.method] || inv.method} · {invoiceDisplayNumber(inv)}
                                    </span>
                                    <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{(parseFloat(inv.total) || 0).toFixed(2)}</span>
                                    <button type="button" title="View pro forma" onClick={() => openModal('view-invoice', inv.id)}
                                      style={{ border: '1px solid var(--bd)', background: 'var(--sur)', borderRadius: 4, cursor: 'pointer', fontSize: 9, padding: '0 4px', lineHeight: 1.6 }}>📄</button>
                                    <button type="button" title="Print pro forma" onClick={() => printInvoice(inv, companySettings, rentals, vehicles)}
                                      style={{ border: '1px solid var(--bd)', background: 'var(--sur)', borderRadius: 4, cursor: 'pointer', fontSize: 9, padding: '0 4px', lineHeight: 1.6 }}>🖨️</button>
                                  </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, paddingTop: 3, borderTop: '1px solid var(--bl-b)' }}>
                                  <span style={{ color: 'var(--tx3)' }}>{dueAtDeskNow > 0 ? 'Still due at desk' : 'Fully paid'}</span>
                                  <span style={{ color: dueAtDeskNow > 0 ? 'var(--am)' : 'var(--gr)', fontWeight: 600 }}>
                                    {dueAtDeskNow > 0 ? `€${dueAtDeskNow.toFixed(2)}` : '✓'}
                                  </span>
                                </div>
                              </div>
                            )}
                            {/* No payment recorded yet but booked online → show expected prepaid */}
                            {paidInv.length === 0 && ['stripe','online','viva','paypal'].includes(r.payMethod) && (
                              <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 5, padding: '6px 8px', margin: '5px 0', fontSize: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: 'var(--tx3)' }}>Pre-paid online</span>
                                  <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{(parseFloat(r.agreedPrice) || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                            {/* Agreed total summary — sits BELOW the breakdown as the bottom line */}
                            <InfoRow label="Agreed Total" value={`€${r.agreedPrice}`}
                              style={{ color: 'var(--gr)', fontSize: 13, fontWeight: 700 }} />
                            {/* Single payment-method row only when there's NO per-method breakdown above
                                (otherwise "Cash on pickup" reads as if the whole total was one method) */}
                            {paidInv.length === 0 && (
                              <InfoRow label="Payment"  value={PAY_LABELS[r.payMethod] || r.payMethod} />
                            )}
                            {r.deposit > 0 && (
                              <InfoRow label="Deposit" value={`€${r.deposit}${r.depositMethod ? ' · ' + r.depositMethod : ''}`} mono />
                            )}
                            {r.payMethod === 'bank' && (
                              <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 5, padding: '5px 8px', margin: '4px 0', fontSize: 10, color: 'var(--am)' }}>
                                🏦 Verify bank transfer before starting
                              </div>
                            )}
                            {r.bookingRef && <InfoRow label="Booking Ref" value={r.bookingRef} mono style={{ color: 'var(--bl)' }} />}
                            {r.insurance && <InfoRow label="Insurance" value={'🛡️ ' + r.insurance} style={{ color: 'var(--bl)' }} />}
                            {r.extras?.length > 0 && <InfoRow label="Extras" value={r.extras.join(', ')} style={{ color: 'var(--am)' }} />}
                          </>
                        )}
                      </div>
                    </div>

                    {/* ── Col 3: Photos + Notes ── */}
                    <div>
                      {hasPhotos ? (
                        <>
                          {photosPre.length > 0 && (
                            <>
                              <ColHead title="📸 Photos at Pickup" />
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 10 }}>
                                {photosPre.map((src, i) => (
                                  <img key={i} src={src} alt="pickup"
                                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 5, border: '1px solid var(--bd)' }}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                          {photosRet.length > 0 && (
                            <>
                              <ColHead title="📸 Photos at Return" />
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 10 }}>
                                {photosRet.map((src, i) => (
                                  <img key={i} src={src} alt="return"
                                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 5, border: '1px solid var(--bd)' }}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <ColHead title="📸 Photos" />
                          <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '6px 0' }}>
                            {r.status === 'booked' ? 'Photos taken at check-in' : 'No photos on file'}
                          </div>
                        </>
                      )}

                      {/* Uploaded docs */}
                      {(r.passportPhoto || r.licencePhoto || r.idFile || r.licenceFile) && (
                        <>
                          <ColHead title="📎 Uploaded Documents" />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            {(r.passportPhoto || r.idFile) && (
                              <div>
                                <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Passport / ID</div>
                                <img
                                  src={r.passportPhoto || (r.idFile instanceof File ? URL.createObjectURL(r.idFile) : r.idFile)}
                                  alt="ID"
                                  style={{ width: '100%', borderRadius: 5, border: '1px solid var(--bd)', maxHeight: 70, objectFit: 'cover' }}
                                />
                              </div>
                            )}
                            {(r.licencePhoto || r.licenceFile) && (
                              <div>
                                <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Licence</div>
                                <img
                                  src={r.licencePhoto || (r.licenceFile instanceof File ? URL.createObjectURL(r.licenceFile) : r.licenceFile)}
                                  alt="Licence"
                                  style={{ width: '100%', borderRadius: 5, border: '1px solid var(--bd)', maxHeight: 70, objectFit: 'cover' }}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Notes */}
                      {r.notes && !r.notes.toLowerCase().includes('online booking') && (
                        <div style={{ marginTop: 8, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '7px 9px', fontSize: 11, color: 'var(--tx2)', lineHeight: 1.5 }}>
                          💬 {r.notes}
                        </div>
                      )}

                      {/* Assignment */}
                      <div style={{ marginTop: 10 }}>
                        <ColHead title="Assignment" />
                        {r.assignedTo ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                            <span style={{ width: 20, height: 20, borderRadius: '50%', background: users.find(u => u.id === r.assignedTo)?.color || 'var(--tx3)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700 }}>
                              {users.find(u => u.id === r.assignedTo)?.initials || '?'}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600 }}>{getUserName(r.assignedTo)}</span>
                            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: r.assignPhase === 'checkout' ? 'var(--am-l)' : 'var(--bl-l)', color: r.assignPhase === 'checkout' ? 'var(--am)' : 'var(--bl)', fontWeight: 600 }}>
                              {r.assignPhase === 'checkout' ? 'Check-out' : 'Check-in'}
                            </span>
                            {canEdit('rentals') && (
                              <button onClick={e => { e.stopPropagation(); unassignRental(r.id); }} style={{ fontSize: 9, color: 'var(--ac)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto', textDecoration: 'underline' }}>Remove</button>
                            )}
                          </div>
                        ) : canEdit('rentals') ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                            <span style={{ fontSize: 10, color: 'var(--am)', fontWeight: 600 }}>⚠️ Unassigned</span>
                            <select
                              style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--bd)', background: 'var(--sur)', flex: 1 }}
                              value=""
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                if (e.target.value) {
                                  const phase = isActive ? 'checkout' : 'checkin';
                                  assignRental(r.id, e.target.value, phase);
                                  showToast(`✅ Assigned to ${getUserName(e.target.value)}`);
                                }
                              }}
                            >
                              <option value="">Assign to...</option>
                              {users.filter(u => {
                                if (u.status !== 'active') return false;
                                const role = roles.find(rl => rl.id === u.roleId);
                                return role?.locked || role?.perms?.rentals?.view === true;
                              }).map(u => (
                                <option key={u.id} value={u.id}>{u.fname} {u.lname} — {(roles.find(rl => rl.id === u.roleId) || {}).name}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--am)', fontWeight: 600, padding: '4px 0' }}>⚠️ Unassigned</div>
                        )}
                        {/* Audit trail */}
                        {(r.checkedInBy || r.checkedOutBy) && (
                          <div style={{ margin: '4px 0', padding: '4px 8px', background: 'var(--sur)', borderRadius: 5, border: '1px solid var(--bd)', fontSize: 10 }}>
                            {r.checkedInBy && <div style={{ color: 'var(--tx2)' }}>✅ Check-in: <strong>{getUserName(r.checkedInBy)}</strong> · {r.checkedInAt}</div>}
                            {r.checkedOutBy && <div style={{ color: 'var(--tx2)', marginTop: 2 }}>🏁 Check-out: <strong>{getUserName(r.checkedOutBy)}</strong> · {r.checkedOutAt}</div>}
                            {r.cashCollectedBy && <div style={{ color: 'var(--gr)', marginTop: 2 }}>💵 Cash: <strong>{getUserName(r.cashCollectedBy)}</strong> · €{(cashPaid > 0 ? cashPaid : parseFloat(r.cashAmount) || 0).toFixed(2)}</div>}
                          </div>
                        )}
                      </div>

                      {/* Quick actions */}
                      <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(r.status === 'active' || r.status === 'overdue') && (
                          <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); openModal(r.type === 'lease' ? 'end-lease' : 'end-rental', r.vid); }}>
                            {r.type === 'lease' ? 'End Lease' : 'End Rental'}
                          </button>
                        )}
                        {r.status === 'booked' && (
                          <button className="btn btn-primary btn-sm" style={{ background: 'var(--gr)', borderColor: 'var(--gr)' }}
                            onClick={e => { e.stopPropagation(); setRentalDraft(r); openModal('check-in'); }}>
                            ✅ Start Rental — Check-in
                          </button>
                        )}
                        {(() => {
                          const linked = invoices.filter(i => i.contract === r.id || i.contractId === r.id);
                          return (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={e => {
                                e.stopPropagation();
                                // Lease OR more than one pro forma → open the list modal.
                                // Exactly one → open it directly. None → toast.
                                if (r.type === 'lease' || linked.length > 1) {
                                  openModal('lease-invoices', r.id);
                                  return;
                                }
                                const one = linked[0]
                                  || (r.invoiceId ? invoices.find(i => i.num === r.invoiceId || i.id === r.invoiceId) : null);
                                if (one) openModal('view-invoice', one.id);
                                else showToast('No pro forma yet for this rental');
                              }}
                            >
                              🧾 {(r.type === 'lease' || linked.length > 1) ? `Pro Formas (${linked.length})` : 'Pro Forma'}
                            </button>
                          );
                        })()}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Available vehicles */}
        {availableVehicles.length > 0 && (
          <div style={{ padding: '8px 14px 4px', background: 'var(--sur2)', borderBottom: '1px solid var(--bd)', fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            ✅ Free — {fmtDayLabel(selectedDate)} ({availableVehicles.length})
          </div>
        )}
        {availableVehicles.map(v => {
          const isOpen = expandedId === v.id;
          return (
            <div key={v.id} style={{ borderBottom: '1px solid var(--bd)' }}>
              <div
                style={{
                  display: 'grid', gridTemplateColumns: GRID,
                  padding: '10px 14px', alignItems: 'center', gap: 8,
                  cursor: 'pointer', background: isOpen ? 'var(--sur2)' : 'transparent',
                }}
                onClick={() => toggle(v.id)}
              >
                <div>
                  <div className="an">{v.icon} {v.name}</div>
                  <div className="ap">{v.type.toUpperCase()} · {v.plate}</div>
                </div>
                <div><StatusPill status="available" /></div>
                <div style={{ fontSize: 12, color: 'var(--tx3)' }}>—</div>
                <div className="dv" style={{ color: 'var(--tx3)' }}>—</div>
                <div className="pv" style={{ color: 'var(--tx3)' }}>
                  €{resolveRate(v, groups, vehicleModels)}/day
                  {v.useGroupPricing && v.groupId && <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--am)' }}>📁</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>—</div>
                <div className="abtns" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button className="ab ab-st" onClick={() => openModal('add-rental', v.id)}>Start</button>
                  <span style={{ fontSize: 11, color: 'var(--tx3)', marginLeft: 2, display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▾</span>
                </div>
              </div>

              {isOpen && (
                <div style={{ background: 'var(--sur2)', borderTop: '1px solid var(--bd)', padding: '14px 16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                    <div>
                      <ColHead title="Vehicle Info" />
                      <InfoRow label="Type"     value={v.type} />
                      <InfoRow label="Plate"    value={v.plate} mono />
                      <InfoRow label="Seats"    value={v.seats} />
                      <InfoRow label="Features" value={v.features} />
                    </div>
                    <div>
                      <ColHead title="Pricing" />
                      {v.useGroupPricing && v.groupId && (() => {
                        const g = (groups || []).find(gg => gg.id === v.groupId);
                        return g ? <div style={{ fontSize: 10, color: 'var(--am)', fontWeight: 600, marginBottom: 5 }}>📁 Group: {g.icon} {g.name}</div> : null;
                      })()}
                      <InfoRow label="Daily rate"   value={`€${resolveRate(v, groups, vehicleModels)}/day`} mono />
                      <InfoRow label="Deposit"      value={`€${v.deposit}`} mono />
                      <InfoRow label="Confirmation" value={v.confirm === 'auto' ? '⚡ Auto' : '👤 Manual'} />
                    </div>
                    <div>
                      <ColHead title="Quick Actions" />
                      <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 6, justifyContent: 'center' }}
                        onClick={e => { e.stopPropagation(); openModal('add-rental', v.id); }}>
                        + Start Rental
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      </>}
    </div>
  );
}
